import React, { useState, useEffect } from 'react';
import { Search, UserPlus, Mail, X, Check, User, Plus, ArrowUp, ArrowDown, GripVertical } from 'lucide-react';
import { toast } from 'react-hot-toast';
import axios from '../api/axios';
import { generateTempId } from '../utils/tempId';

const FirmantesSelector = ({ onFirmantesChange, firmantes = [], disabled = false, onSearchFocus, onSearchBlur, requiereOrdenFirma = false, onRequiereOrdenFirmaChange, arrastrarCampos = false }) => {
    const [usuarios, setUsuarios] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [newFirmante, setNewFirmante] = useState({
        email: '',
        nombre_completo: '',
        orden_firma: 1
    });
    const [showAddForm, setShowAddForm] = useState(false);
    const [loading, setLoading] = useState(false);

    // Cargar usuarios de la base de datos
    useEffect(() => {
        const fetchUsuarios = async () => {
            try {
                const response = await axios.get('/api/users/');
                setUsuarios(response.data);
            } catch (error) {
                console.error('Error al cargar usuarios:', error);
                toast.error('Error al cargar la lista de usuarios');
            }
        };
        fetchUsuarios();
    }, []);

    // Filtrar usuarios por término de búsqueda
    const filteredUsuarios = usuarios.filter(usuario =>
        usuario.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
        usuario.apellido.toLowerCase().includes(searchTerm.toLowerCase()) ||
        usuario.email.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Agregar firmante existente
    const handleAddExistingFirmante = (usuario) => {
        const nuevoFirmante = {
            temp_id: generateTempId(),
            user_id: usuario.id,
            email: usuario.email,
            nombre_completo: `${usuario.nombre} ${usuario.apellido}`,
            orden_firma: requiereOrdenFirma ? firmantes.length + 1 : 1,
            coordenadas_x: null,
            coordenadas_y: null,
            pagina_firma: 1,
            tipo_firma: 'texto'
        };

        const nuevosFirmantes = [...firmantes, nuevoFirmante];
        if (requiereOrdenFirma) {
            // Asegurar que todos tengan orden correcto
            nuevosFirmantes.forEach((f, i) => {
                f.orden_firma = i + 1;
            });
        }
        onFirmantesChange(nuevosFirmantes);
        setSearchTerm('');
        toast.success(`${usuario.nombre} ${usuario.apellido} agregado como firmante`);
    };

    // Agregar nuevo firmante por email
    const handleAddNewFirmante = async () => {
        if (!newFirmante.email || !newFirmante.nombre_completo) {
            toast.error('Por favor completa todos los campos');
            return;
        }

        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newFirmante.email)) {
            toast.error('Por favor ingresa un email válido');
            return;
        }

        const nuevoFirmante = {
            temp_id: generateTempId(),
            user_id: null, // No está registrado aún
            email: newFirmante.email,
            nombre_completo: newFirmante.nombre_completo,
            orden_firma: requiereOrdenFirma ? firmantes.length + 1 : 1,
            coordenadas_x: null,
            coordenadas_y: null,
            pagina_firma: 1,
            tipo_firma: 'texto'
        };

        const nuevosFirmantes = [...firmantes, nuevoFirmante];
        if (requiereOrdenFirma) {
            // Asegurar que todos tengan orden correcto
            nuevosFirmantes.forEach((f, i) => {
                f.orden_firma = i + 1;
            });
        }
        onFirmantesChange(nuevosFirmantes);
        setNewFirmante({ email: '', nombre_completo: '', orden_firma: 1 });
        setShowAddForm(false);
        toast.success(`${newFirmante.nombre_completo} agregado como firmante`);
    };

    // Remover firmante
    const handleRemoveFirmante = (index) => {
        const nuevosFirmantes = firmantes.filter((_, i) => i !== index);
        // Reordenar números de orden si requiere orden
        if (requiereOrdenFirma) {
            nuevosFirmantes.forEach((f, i) => {
                f.orden_firma = i + 1;
            });
        }
        onFirmantesChange(nuevosFirmantes);
        toast.success('Firmante removido');
    };

    // Mover firmante arriba
    const handleMoveUp = (index) => {
        if (index === 0) return;
        const nuevosFirmantes = [...firmantes];
        [nuevosFirmantes[index - 1], nuevosFirmantes[index]] = [nuevosFirmantes[index], nuevosFirmantes[index - 1]];
        // Actualizar orden
        nuevosFirmantes.forEach((f, i) => {
            f.orden_firma = i + 1;
        });
        onFirmantesChange(nuevosFirmantes);
    };

    // Mover firmante abajo
    const handleMoveDown = (index) => {
        if (index === firmantes.length - 1) return;
        const nuevosFirmantes = [...firmantes];
        [nuevosFirmantes[index], nuevosFirmantes[index + 1]] = [nuevosFirmantes[index + 1], nuevosFirmantes[index]];
        // Actualizar orden
        nuevosFirmantes.forEach((f, i) => {
            f.orden_firma = i + 1;
        });
        onFirmantesChange(nuevosFirmantes);
    };

    return (
        <div id="tour-firmantes" className="space-y-5">
            {/* Título y Checkbox de Orden */}
            <div className="flex items-center justify-between gap-4 flex-wrap">
                <h3 className="text-lg font-semibold text-gray-900">
                    Firmantes ({firmantes.length})
                </h3>
                <div id="tour-orden-firmas">
                <label 
                    className={`flex items-center gap-2 flex-shrink-0 ${firmantes.length > 1 ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'}`}
                    title={firmantes.length <= 1 ? 'Agrega al menos 2 firmantes para activar el orden de firmas' : 'Activar orden secuencial de firmas'}
                >
                    <input
                        type="checkbox"
                        checked={requiereOrdenFirma}
                        onChange={(e) => {
                            if (firmantes.length <= 1) return; // No permitir cambiar si hay menos de 2 firmantes
                            console.log('[FirmantesSelector] Checkbox cambiado:', e.target.checked)
                            if (onRequiereOrdenFirmaChange) {
                                onRequiereOrdenFirmaChange(e.target.checked);
                                console.log('[FirmantesSelector] Estado actualizado a:', e.target.checked)
                            }
                            // Si se desactiva, resetear todos los órdenes a 1
                            if (!e.target.checked) {
                                const nuevosFirmantes = firmantes.map(f => ({ ...f, orden_firma: 1 }));
                                onFirmantesChange(nuevosFirmantes);
                            } else {
                                // Si se activa, asignar orden secuencial
                                const nuevosFirmantes = firmantes.map((f, i) => ({ ...f, orden_firma: i + 1 }));
                                onFirmantesChange(nuevosFirmantes);
                            }
                        }}
                        disabled={disabled || firmantes.length <= 1}
                        className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                    />
                    <span className="text-sm font-medium text-gray-700 whitespace-nowrap">
                        Orden de firmas
                        {firmantes.length <= 1 && (
                            <span className="ml-1 text-xs text-gray-500">(requiere 2+ firmantes)</span>
                        )}
                    </span>
                </label>
                </div>
            </div>

            {/* Búsqueda de usuarios existentes */}
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                    Buscar usuarios existentes
                </label>
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => {
                            setSearchTerm(e.target.value)
                            // Minimizar sidebar cuando hay texto de búsqueda
                            if (e.target.value.trim() && onSearchFocus) {
                                onSearchFocus()
                            } else if (!e.target.value.trim() && onSearchBlur) {
                                onSearchBlur()
                            }
                        }}
                        onFocus={() => {
                            // Minimizar sidebar al enfocar el input
                            if (onSearchFocus) onSearchFocus()
                        }}
                        onBlur={(e) => {
                            // Restaurar sidebar solo si no hay texto y no hay resultados visibles
                            if (!e.target.value.trim() && onSearchBlur) {
                                setTimeout(() => {
                                    // Verificar que el input aún no tenga foco
                                    if (document.activeElement !== e.target && !e.target.value.trim()) {
                                        onSearchBlur()
                                    }
                                }, 300)
                            }
                        }}
                        disabled={disabled}
                        className={`w-full pl-10 pr-4 py-2.5 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all ${
                            disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'
                        }`}
                        placeholder="Escribe para buscar..."
                    />
                </div>

                {/* Lista de usuarios filtrados */}
                {searchTerm && !disabled && (
                    <div className="mt-3 bg-white border-2 border-gray-200 rounded-lg shadow-lg max-h-80 overflow-y-auto">
                        {filteredUsuarios.length > 0 ? (
                            <div className="divide-y divide-gray-100">
                                {filteredUsuarios.map((usuario) => (
                                    <div
                                        key={usuario.id}
                                        className="flex items-center justify-between p-4 hover:bg-gradient-to-r hover:from-primary-50 hover:to-blue-50 cursor-pointer transition-all duration-200 group"
                                        onClick={() => handleAddExistingFirmante(usuario)}
                                    >
                                        <div className="flex items-center gap-3 flex-1 min-w-0">
                                            <div className="w-8 h-8 bg-gradient-to-br from-primary-500 to-primary-600 rounded-full flex items-center justify-center shadow-md flex-shrink-0 group-hover:scale-110 transition-transform">
                                                <User className="h-4 w-4 text-white" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="font-semibold text-gray-900 text-base truncate">
                                                    {usuario.nombre} {usuario.apellido}
                                                </p>
                                                <p className="text-sm text-gray-600 truncate mt-0.5">{usuario.email}</p>
                                            </div>
                                        </div>
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleAddExistingFirmante(usuario);
                                            }}
                                            className="ml-3 flex-shrink-0 w-6 h-6 flex items-center justify-center bg-primary-600 text-white rounded-full hover:bg-primary-700 transition-colors shadow-sm hover:shadow-md group-hover:scale-110"
                                            title="Agregar firmante"
                                        >
                                            <Plus className="h-3 w-3" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="p-8 text-center text-gray-500">
                                <User className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                                <p className="text-sm font-medium">No se encontraron usuarios</p>
                                <p className="text-xs text-gray-400 mt-1">Intenta con otro término de búsqueda</p>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Lista de firmantes seleccionados */}
            {firmantes.length > 0 && (
                <div className="pt-4 border-t border-gray-200">
                    <h4 className="font-semibold text-gray-900 mb-3">Firmantes Agregados</h4>
                    {arrastrarCampos && !disabled && (
                        <p className="text-xs text-gray-500 mb-3">Arrastra un firmante sobre el PDF para ubicar su firma. Puedes soltarlo varias veces.</p>
                    )}
                    <div className="space-y-3">
                        {firmantes.map((firmante, index) => (
                            <div
                                key={index}
                                className="flex items-center justify-between p-4 bg-gradient-to-r from-white to-gray-50 border-2 border-gray-200 rounded-xl hover:border-primary-300 transition-all shadow-sm overflow-hidden"
                            >
                                <div
                                    className={`flex items-center gap-3 flex-1 min-w-0 ${arrastrarCampos && !disabled ? 'cursor-grab active:cursor-grabbing' : ''}`}
                                    draggable={arrastrarCampos && !disabled}
                                    data-firmante-email={firmante.email}
                                    onDragStart={(event) => {
                                        if (!arrastrarCampos || disabled) {
                                            event.preventDefault()
                                            return
                                        }
                                        event.dataTransfer.effectAllowed = 'copy'
                                        event.dataTransfer.setData('application/x-pdfsigner-firmante', JSON.stringify({
                                            email: firmante.email,
                                            nombre: firmante.nombre_completo,
                                            temp_id: firmante.temp_id
                                        }))
                                        event.dataTransfer.setData('text/plain', firmante.nombre_completo || firmante.email)
                                    }}
                                >
                                    {arrastrarCampos && !disabled && (
                                        <GripVertical className="h-4 w-4 text-gray-400 flex-shrink-0" aria-hidden="true" />
                                    )}
                                    <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-primary-600 rounded-full flex items-center justify-center shadow-sm flex-shrink-0">
                                        <span className="text-sm font-bold text-white">
                                            {firmante.orden_firma}
                                        </span>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="font-semibold text-gray-900 truncate">
                                            {firmante.nombre_completo}
                                        </p>
                                        <p className="text-sm text-gray-600 truncate">{firmante.email}</p>
                                        <div className="mt-1">
                                            {firmante.user_id ? (
                                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-700 font-medium">
                                                    <Check className="h-3 w-3 mr-1" />
                                                    Registrado
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-yellow-100 text-yellow-700 font-medium">
                                                    <Mail className="h-3 w-3 mr-1" />
                                                    Se registrará
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 ml-3 flex-shrink-0">
                                    {requiereOrdenFirma && (
                                        <div className="flex flex-col gap-1">
                                            <button
                                                onClick={() => handleMoveUp(index)}
                                                disabled={disabled || index === 0}
                                                className={`p-1 rounded transition-colors ${
                                                    disabled || index === 0
                                                        ? 'text-gray-300 cursor-not-allowed' 
                                                        : 'text-blue-500 hover:bg-blue-50 hover:text-blue-700'
                                                }`}
                                                title="Mover arriba"
                                            >
                                                <ArrowUp className="h-4 w-4" />
                                            </button>
                                            <button
                                                onClick={() => handleMoveDown(index)}
                                                disabled={disabled || index === firmantes.length - 1}
                                                className={`p-1 rounded transition-colors ${
                                                    disabled || index === firmantes.length - 1
                                                        ? 'text-gray-300 cursor-not-allowed' 
                                                        : 'text-blue-500 hover:bg-blue-50 hover:text-blue-700'
                                                }`}
                                                title="Mover abajo"
                                            >
                                                <ArrowDown className="h-4 w-4" />
                                            </button>
                                        </div>
                                    )}
                                    <button
                                        onClick={() => handleRemoveFirmante(index)}
                                        disabled={disabled}
                                        className={`p-2 rounded-lg transition-colors ${
                                            disabled 
                                                ? 'text-gray-300 cursor-not-allowed' 
                                                : 'text-red-500 hover:bg-red-50 hover:text-red-700'
                                        }`}
                                        title="Eliminar firmante"
                                    >
                                        <X className="h-5 w-5" />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Botón Agregar - Siempre al final */}
            <div className="pt-4 border-t border-gray-200">
                <button
                    id="tour-invitar-firmante"
                    onClick={() => setShowAddForm(!showAddForm)}
                    disabled={disabled}
                    className={`w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg transition-all duration-200 ${
                        disabled 
                            ? 'bg-gray-200 text-gray-400 cursor-not-allowed' 
                            : 'bg-gradient-to-r from-primary-600 to-primary-700 text-white hover:from-primary-700 hover:to-primary-800 shadow-sm hover:shadow-md'
                    }`}
                >
                    <UserPlus className="h-5 w-5" />
                    <span className="font-medium">Invitar Usuario Nuevo</span>
                </button>
            </div>

            {/* Modal para agregar nuevo firmante */}
            {showAddForm && !disabled && (
                <div 
                    className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
                    onClick={() => setShowAddForm(false)}
                >
                    <div 
                        className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 transform transition-all"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="p-6">
                            {/* Header del modal */}
                            <div className="flex items-center justify-between mb-6">
                                <h4 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                                    <Mail className="h-6 w-6 text-primary-600" />
                                    Invitar a Firmar
                                </h4>
                                <button
                                    onClick={() => setShowAddForm(false)}
                                    className="text-gray-400 hover:text-gray-600 transition-colors p-1 hover:bg-gray-100 rounded-lg"
                                >
                                    <X className="h-5 w-5" />
                                </button>
                            </div>
                            
                            {/* Formulario */}
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Email *
                                    </label>
                                    <input
                                        type="email"
                                        value={newFirmante.email}
                                        onChange={(e) => setNewFirmante({...newFirmante, email: e.target.value})}
                                        className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all bg-white"
                                        placeholder="correo@ejemplo.com"
                                        autoFocus
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Nombre Completo *
                                    </label>
                                    <input
                                        type="text"
                                        value={newFirmante.nombre_completo}
                                        onChange={(e) => setNewFirmante({...newFirmante, nombre_completo: e.target.value})}
                                        className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all bg-white"
                                        placeholder="Juan Pérez"
                                    />
                                </div>
                                
                                {/* Botones del modal */}
                                <div className="flex gap-3 pt-4">
                                    <button
                                        onClick={() => {
                                            setShowAddForm(false)
                                            setNewFirmante({ email: '', nombre_completo: '', orden_firma: 1 })
                                        }}
                                        className="flex-1 px-4 py-2.5 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                                    >
                                        Cancelar
                                    </button>
                                    <button
                                        onClick={handleAddNewFirmante}
                                        className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium shadow-sm hover:shadow-md"
                                    >
                                        <Check className="h-5 w-5" />
                                        Agregar Firmante
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default FirmantesSelector;


